import { addDays, differenceInCalendarDays, format } from 'date-fns';
import { isHiddenScheduleClient } from '../../coachSchedules/hiddenClients';
import { CoachScheduleDoc, CoachSchedulesMeta } from '../../coachSchedules/types';
import { dayKey, ScheduledSession, slotLabel, sortSessions } from '../schedule';
import { formatPersonName, locationLabel } from '../tasks/clientRecord';
import { ClientWork, DashboardClient } from '../tasks/types';

/**
 * Today's source switch (design §8): a coach with `mindbody_schedule` reads their day from
 * `coach_schedules`, written from Mindbody at 05:00 and 12:00, and falls back to the client
 * list's `upcoming_appointments` only when that day's schedule isn't there.
 *
 * Dates and times are the browser's local wall clock, as everywhere else on Today. The coaches
 * are in Singapore, and Mindbody's `start_local` is Singapore wall clock with no offset.
 */

export type LoadState = 'loading' | 'ready' | 'error';

/** What the listeners have delivered so far. */
export interface LiveSchedule {
  enabled: boolean;
  meta: { status: LoadState; value: CoachSchedulesMeta | null };
  /** Keyed by yyyy-mm-dd; `schedule` null on a ready day means the coach has no sessions. */
  days: Record<string, { status: LoadState; schedule: CoachScheduleDoc | null }>;
}

export type DaySource =
  | { kind: 'schedule'; schedule: CoachScheduleDoc | null; syncedAt: Date }
  | { kind: 'loading' }
  /** off: the flag is off. not-yet: tomorrow before the 05:00 run. unavailable: anything else went wrong. */
  | { kind: 'fallback'; reason: 'off' | 'not-yet' | 'unavailable' };

/** The scheduled runs (SGT). */
const SYNC_HOURS = [5, 12];
/** A run this late turns the "Updated" line amber (design §6.5). */
const LATE_AFTER_MS = 30 * 60 * 1000;
const MUTED_STATUSES: ReadonlySet<string> = new Set(['LateCancelled', 'NoShow']);

export const isCountableStatus = (status: string | null) => !status || !MUTED_STATUSES.has(status);

/** Chips are shown only for statuses a 05:00 or 12:00 snapshot can stand behind; check-ins move all day. */
export const STATUS_CHIPS: Record<string, string> = {
  Confirmed: 'Confirmed',
  Requested: 'Requested',
  LateCancelled: 'Late cancel',
  NoShow: 'No show',
};

/** Which source a day reads from. */
export function daySource(live: LiveSchedule, date: string, now: Date): DaySource {
  if (!live.enabled) return { kind: 'fallback', reason: 'off' };
  if (live.meta.status === 'loading') return { kind: 'loading' };
  const state = live.meta.status === 'ready' ? live.meta.value?.dates[date] : undefined;
  if (!state || !state.synced_at) {
    // Until the 05:00 run (and its first half hour), tomorrow is simply not synced yet: say when it arrives.
    const beforeFirstRun = now.getHours() * 60 + now.getMinutes() < SYNC_HOURS[0] * 60 + LATE_AFTER_MS / 60000;
    const notYet = live.meta.value !== null && date === dayKey(addDays(now, 1)) && beforeFirstRun;
    return { kind: 'fallback', reason: notYet ? 'not-yet' : 'unavailable' };
  }
  const day = live.days[date];
  if (!day || day.status === 'loading') return { kind: 'loading' };
  if (day.status === 'error') return { kind: 'fallback', reason: 'unavailable' };
  return { kind: 'schedule', schedule: day.schedule, syncedAt: state.synced_at };
}

/** The most recent 05:00 or 12:00 at or before `now`. */
export function lastScheduledRun(now: Date): Date {
  const runs = [-1, 0].flatMap((offset) => {
    const day = addDays(now, offset);
    return SYNC_HOURS.map((hour) => new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour, 0, 0, 0));
  });
  return runs.filter((run) => run.getTime() <= now.getTime()).sort((a, b) => b.getTime() - a.getTime())[0];
}

/** "Updated 5:00 AM", "Updated 12:00 PM yesterday"; `late` names the run that hasn't arrived after half an hour. */
export function freshness(syncedAt: Date, now: Date): { label: string; late: string | null } {
  const days = differenceInCalendarDays(now, syncedAt);
  const time = format(syncedAt, 'h:mm a');
  const when = days <= 0 ? time : days === 1 ? `${time} yesterday` : format(syncedAt, 'EEE d MMM, h:mm a');
  const expected = lastScheduledRun(now);
  const late = syncedAt.getTime() < expected.getTime() && now.getTime() - expected.getTime() > LATE_AFTER_MS;
  return { label: `Updated ${when}`, late: late ? `The ${format(expected, 'h:mm a')} update is late` : null };
}

/** "2026-10-29T07:00:00" → the date and wall-clock time as booked. */
export function wallClock(startLocal: string): { date: string; time: { hours: number; minutes: number } | null } {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/.exec(startLocal);
  if (!match) return { date: startLocal.slice(0, 10), time: null };
  const hours = parseInt(match[2], 10);
  const minutes = parseInt(match[3], 10);
  return { date: match[1], time: hours <= 23 && minutes <= 59 ? { hours, minutes } : null };
}

/** Mindbody's location names carry the brand and the address: "UFIT Orchard" reads "Orchard", CBD reads "Club Street". */
const scheduleLocation = (name: string | null) => locationLabel(name).replace(/^UFIT\s+/i, '');

/**
 * A coach's day from their Mindbody schedule, joined to the clients the dashboard has loaded.
 * A client the list doesn't have yet becomes a schedule-only card (`client` null); the hidden
 * test client is dropped for everyone but its test logins.
 */
export function sessionsFromSchedule(
  schedule: CoachScheduleDoc,
  clientsById: Map<string, DashboardClient>,
  workByClient: Map<string, ClientWork>,
  coachId: string,
  now: Date,
  viewerEmail: string | null
): ScheduledSession[] {
  const isToday = schedule.date === dayKey(now);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const sessions = schedule.sessions
    .filter((session) => !isHiddenScheduleClient(session.client_id, viewerEmail))
    .map((session): ScheduledSession => {
      const { date, time } = wallClock(session.start_local);
      const client = clientsById.get(session.client_id) ?? null;
      const collaboration = client !== null && client.coachId !== coachId;
      return {
        key: `${session.appointment_id || session.client_id}-${session.start_local}`,
        client,
        clientId: session.client_id,
        clientName: client?.name || formatPersonName(session.client_name) || `Client #${session.client_id}`,
        appointment: { coachId: Number(coachId), coachName: '', location: scheduleLocation(session.location_name), date, time },
        work: collaboration ? null : workByClient.get(session.client_id) ?? null,
        // From the clock, never the status: Mindbody marks some sessions Completed before they start.
        past: isToday && time !== null && time.hours * 60 + time.minutes < nowMinutes,
        collaboration,
        sessionTypeName: session.session_type_name,
        durationMin: session.duration_min,
        status: session.status || null,
        countable: isCountableStatus(session.status),
      };
    });
  return sortSessions(sessions);
}

/** Client ids in these schedules, the hidden test client left out: the ones Today must be able to name. */
export function scheduleClientIds(schedules: (CoachScheduleDoc | null)[], viewerEmail: string | null): string[] {
  const ids = new Set<string>();
  schedules.forEach((schedule) =>
    schedule?.sessions.forEach((session) => {
      if (!isHiddenScheduleClient(session.client_id, viewerEmail)) ids.add(session.client_id);
    })
  );
  return Array.from(ids);
}

export interface Slot {
  date: string;
  time: { hours: number; minutes: number } | null;
}

const slotKey = (slot: Slot) => slot.date + (slot.time ? String(slot.time.hours * 60 + slot.time.minutes).padStart(4, '0') : '9999');

/** Each client's next booked session with this coach in the Mindbody schedule (today's still to come, then tomorrow's). */
export function scheduleNextSlots(today: ScheduledSession[] | null, tomorrow: ScheduledSession[] | null): Map<string, Slot> {
  const slots = new Map<string, Slot>();
  [...(today ?? []).filter((session) => !session.past), ...(tomorrow ?? [])].forEach((session) => {
    if (session.countable && !slots.has(session.clientId)) slots.set(session.clientId, { date: session.appointment.date, time: session.appointment.time });
  });
  return slots;
}

/**
 * "Next session" for a waiting client: the earlier of their next Mindbody session with this coach
 * and their next booking in the client list. On days the schedule covers, the list's bookings with
 * this coach are skipped (the schedule is newer); bookings with other coaches still count.
 */
export function waitingNextLabel(client: DashboardClient, coachId: string, now: Date, slots: Map<string, Slot>, scheduledDates: ReadonlySet<string>): string {
  const today = dayKey(now);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const coach = Number(coachId);
  const listed = client.appointments.find(
    (a) =>
      (a.date > today || (a.date === today && (a.time ? a.time.hours * 60 + a.time.minutes : Number.POSITIVE_INFINITY) >= nowMinutes)) &&
      !(a.coachId === coach && scheduledDates.has(a.date))
  );
  const candidates: Slot[] = [];
  if (listed) candidates.push({ date: listed.date, time: listed.time });
  const scheduled = slots.get(client.clientId);
  if (scheduled) candidates.push(scheduled);
  if (candidates.length === 0) return 'None booked';
  return slotLabel(candidates.sort((a, b) => slotKey(a).localeCompare(slotKey(b)))[0], now);
}
